import { create } from "@bufbuild/protobuf";
import { timestampFromDate } from "@bufbuild/protobuf/wkt";
import { queryOptions, useQuery, useMutation } from "@tanstack/react-query";

import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { keyWithPrincipal } from "@/queries/principal-key";
import type {
    GetCreditUsageReportResponse,
    DailyCreditUsage,
    UserCreditUsage,
    CreditsByType,
} from "gitpod-next-api/gitpod/v1/billing_pb";
import { CreditUsageExportGroupBy } from "gitpod-next-api/gitpod/v1/billing_pb";
import { DateRangeSchema } from "gitpod-next-api/gitpod/v1/usage_pb";

export type PlainGetCreditUsageReportResponse = GetCreditUsageReportResponse;
export type PlainDailyCreditUsage = DailyCreditUsage;
export type PlainUserCreditUsage = UserCreditUsage;
export type PlainCreditsByType = CreditsByType;

const enterpriseUsageKeys = {
    report: (organizationId: string, startISO: string, endISO: string) =>
        keyWithPrincipal(["enterprise-usage", "report", organizationId, startISO, endISO]),
};

export function useEnterpriseCreditUsageReportOptions(organizationId: string, startDate: Date, endDate: Date) {
    const api = useGitpodAPI();
    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();

    return queryOptions({
        queryKey: enterpriseUsageKeys.report(organizationId, startISO, endISO),
        queryFn: async () => {
            const response = await api.billingService.getCreditUsageReport({
                organizationId,
                dateRange: create(DateRangeSchema, {
                    startTime: timestampFromDate(startDate),
                    endTime: timestampFromDate(endDate),
                }),
            });
            return response;
        },
        throwOnError: false,
        enabled: !!organizationId,
    });
}

export function useEnterpriseCreditUsageReport(organizationId: string, startDate: Date, endDate: Date) {
    return useQuery(useEnterpriseCreditUsageReportOptions(organizationId, startDate, endDate));
}

export function useExportCreditUsage() {
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async ({
            organizationId,
            startDate,
            endDate,
            groupBy,
        }: {
            organizationId: string;
            startDate: Date;
            endDate: Date;
            groupBy?: CreditUsageExportGroupBy;
        }) => {
            const response = await api.billingService.getCreditUsageExport({
                organizationId,
                dateRange: create(DateRangeSchema, {
                    startTime: timestampFromDate(startDate),
                    endTime: timestampFromDate(endDate),
                }),
                groupBy: groupBy ?? CreditUsageExportGroupBy.DAILY_SUMMARY,
            });
            return response.downloadUrl;
        },
    });
}
